/* global PiniaSetupStoreHelpers */
import type { UnwrapRef } from 'vue';
import type { Writable } from 'type-fest';
import type { ResultItem } from '@/utils/file';
import type { RequiredKeys } from '@/utils/types';
import type {
  InitialData as BookedAirRoadSearchFormInitialData,
  ValidData as BookedAirRoadSearchFormValidData,
} from '@/components/searchForms/BookedAirRoadSearchForm.vue';
import type { BookedAirRoadViewDataComposable } from '@/composables/data/useBookedAirRoadViewData';
import type { CommonRouteQueryComposable } from '@/composables/routeQueries/useCommonRouteQuery';

import { computed, reactive, ref, toRefs, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { defineStore } from 'pinia';
import _cloneDeep from 'lodash/cloneDeep';
import _findKey from 'lodash/findKey';
import _isEqual from 'lodash/isEqual';

import useDownloadCsv from '@/composables/useDownloadCsv';
import { fetchPostAirroadJobsDownloadLabelsFile } from '@/helpers/fetchers';
import * as locationQueryHelpers from '@/helpers/locationQuery';
import * as bookedAirRoadTableHelpers from '@/helpers/tables/bookedAirRoad';
import * as routeNames from '@/constants/routeNames';

interface DefPrivateBookedAirRoadViewState {
  data: RequiredKeys<
    Partial<Writable<UnwrapRef<BookedAirRoadViewDataComposable>>>
  >;
  query: RequiredKeys<{
    curPage: number | undefined;
    pageSize: number | undefined;
    dates: [Date, Date] | undefined;
    status: number | undefined;
    customer: string | undefined;
    search: string | undefined;
    dateType: string | undefined;
  }>;
}

interface DefBookedAirRoadViewState {
  selectedResults: Record<string, boolean>;
}

const defPrivateBookedAirRoadViewState: DefPrivateBookedAirRoadViewState = {
  data: {
    isFetchingAirRoadBookedJobsData: undefined,
    airRoadBookedJobsData: undefined,
    airRoadBookedJobsDataError: undefined,
    refreshAirRoadBookedJobsData: undefined,
    reload: undefined,
  },
  query: {
    curPage: undefined,
    pageSize: undefined,
    dates: undefined,
    status: undefined,
    customer: undefined,
    search: undefined,
    dateType: undefined,
  },
};

const defBookedAirRoadViewState: DefBookedAirRoadViewState = {
  selectedResults: {},
};

function privateBookedAirRoadViewStoreSetup(helpers: PiniaSetupStoreHelpers) {
  const state = reactive(_cloneDeep(defPrivateBookedAirRoadViewState));
  const route = useRoute();

  async function $reset(): Promise<void> {
    const reload = state.data.reload;
    const query = state.query;
    Object.assign(state, _cloneDeep(defPrivateBookedAirRoadViewState));
    if (route.name === routeNames.AIR_ROAD_BOOKED) {
      state.query = query; // preserve query while on the page
      if (reload) {
        setData(reload()); // reload data while on the page
      }
    }
  }

  function setData(viewData: BookedAirRoadViewDataComposable) {
    state.data = reactive(viewData);
  }

  function setQuery(routeQuery: CommonRouteQueryComposable) {
    state.query = reactive({
      curPage: routeQuery.queryCurPage,
      pageSize: routeQuery.queryPageSize,
      dates: routeQuery.queryDates,
      status: routeQuery.queryStatus,
      customer: routeQuery.queryCustomer,
      search: routeQuery.querySearch,
      dateType: routeQuery.queryDateType,
    });
  }

  watch(
    () => route.name,
    function () {
      if (route.name !== routeNames.AIR_ROAD_BOOKED) {
        void $reset(); // reset when leave the page
      }
    },
    { immediate: true },
  );

  return {
    ...toRefs(state),
    $reset,
    setData,
    setQuery,
  };
}

function bookedAirRoadViewStoreSetup(helpers: PiniaSetupStoreHelpers) {
  const state = reactive(_cloneDeep(defBookedAirRoadViewState));
  const route = useRoute();
  const router = useRouter();

  const _bookedAirRoadViewStore = usePrivateBookedAirRoadViewStore();

  const data = computed(function () {
    return _bookedAirRoadViewStore.data;
  });

  const query = computed(function () {
    return _bookedAirRoadViewStore.query;
  });

  const bookedAirRoadTableData = computed(function () {
    return bookedAirRoadTableHelpers.getTableData(
      data.value.airRoadBookedJobsData,
    );
  });

  const initialData = computed<BookedAirRoadSearchFormInitialData>(() => {
    return {
      dates: query.value.dates,
      status: query.value.status,
      customer: query.value.customer,
      search: query.value.search,
      dateType: query.value.dateType,
    };
  });

  const isSelected = computed(function () {
    return !!_findKey(state.selectedResults, (selected) => selected);
  });

  const isFetchingBulkLabels = ref(false);

  const { isDownloadingCSV, downloadCsv } = useDownloadCsv();

  const resultsForCsv = computed<ResultItem[]>(() => {
    const results = data.value.airRoadBookedJobsData?.results;
    if (!Array.isArray(results)) return [];
    return results.map((item) => {
      const {
        items,
        json_used_for_booking: jsonUsedForBooking,
        special_options: specialOptions,
        stops,
        ...resultItem
      } = item;
      return resultItem;
    });
  });

  async function $reset(): Promise<void> {
    Object.assign(state, _cloneDeep(defBookedAirRoadViewState));
    _bookedAirRoadViewStore.$reset();
  }

  function init(
    viewData: BookedAirRoadViewDataComposable,
    routeQuery: CommonRouteQueryComposable,
  ) {
    _bookedAirRoadViewStore.setData(viewData);
    _bookedAirRoadViewStore.setQuery(routeQuery);
  }

  function search(formData: BookedAirRoadSearchFormValidData): void {
    const query = locationQueryHelpers.buildBookedAirRoadSearchLocationQuery(
      route.query,
      formData,
    );
    const isReplace = _isEqual(route.query, query);
    void router.push({
      name: routeNames.AIR_ROAD_BOOKED,
      query,
      force: true,
      replace: isReplace,
    });
  }

  function downloadCSV() {
    downloadCsv(resultsForCsv.value, 'bookedAirRoadJobs');
  }

  async function handleDownloadBulkLabelsClick() {
    if (isFetchingBulkLabels.value) return;
    if (!isSelected.value) return;
    isFetchingBulkLabels.value = true;
    const ids: number[] = [];
    const connotes: string[] = [];
    for (const [key, value] of Object.entries(state.selectedResults)) {
      if (value) {
        const id = parseInt(key);
        ids.push(id);
        const connote =
          data.value.airRoadBookedJobsData?.results?.find(
            (job) => job.id === id,
          )?.connote || '';
        connotes.push(connote);
      }
    }
    await fetchPostAirroadJobsDownloadLabelsFile(
      connotes.join('-'),
      ids,
    ).finally(() => {
      isFetchingBulkLabels.value = false;
    });
  }

  watch(
    () => data.value.airRoadBookedJobsData,
    function () {
      state.selectedResults = {};
    },
  );

  return {
    ...toRefs(state),
    data,
    query,
    bookedAirRoadTableData,
    initialData,
    isSelected,
    isFetchingBulkLabels,
    isDownloadingCSV,
    $reset,
    init,
    search,
    downloadCSV,
    handleDownloadBulkLabelsClick,
  };
}

const usePrivateBookedAirRoadViewStore = defineStore(
  '_bookedAirRoadView',
  privateBookedAirRoadViewStoreSetup,
);

export const useBookedAirRoadViewStore = defineStore(
  'bookedAirRoadView',
  bookedAirRoadViewStoreSetup,
);
